import { ChangeDetectionStrategy, Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService, AuthStore } from '@auth';
import { MsalBroadcastService, MsalModule, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { GoogleTagManagerService } from '@core/services/google-tag-manager.service';
import { NotificationActionType } from '@shared/models/notification/notification-action-type.enum';
import { UserProfileService } from '@shared/services/user-profile.service';
import { AssetQueryStore } from '@store/asset-query.store';
import { NotificationSSEStore } from '@store/notification-sse.store';
import { ToastComponent, ToastService } from '@ui';
import { map, noop } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, MsalModule, ToastComponent],
  providers: [AuthService, UserProfileService],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent implements OnInit {
  public readonly toasts = this.toastService.toasts;

  public constructor(
    private msalAuthService: MsalService,
    private authService: AuthService,
    private msalBroadcastService: MsalBroadcastService,
    private destroyRef: DestroyRef,
    private authStore: AuthStore,
    private userProfileService: UserProfileService,
    private toastService: ToastService,
    private assetQueryStore: AssetQueryStore,
    private notificationSSEStore: NotificationSSEStore,
    private router: Router,
    private googleTagManager: GoogleTagManagerService
  ) {}

  public ngOnInit(): void {
    this.#setupMsalRedirect();
    this.#setupCompletedLoginStream();
    this.#setupRefreshProfileUnits();
    this.#initPageViewTracking();
  }

  #initPageViewTracking(): void {
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe({
      next: (event: NavigationEnd) => {
        this.googleTagManager.pushEvent('page_view', {
          page_path: event.urlAfterRedirects,
          page_title: this.#getPageTitle(event.urlAfterRedirects)
        });
      }
    });
  }

  #getPageTitle(url: string): string {
    const path = url.split('?')[0];
    const segments = path.split('/').filter(Boolean);
    return segments.length ? segments[segments.length - 1] : 'Home';
  }

  #setupRefreshProfileUnits(): void {
    this.notificationSSEStore.event$
      .pipe(
        filter(data =>
          [NotificationActionType.ADDED_TO_UNIT, NotificationActionType.REMOVED_FROM_UNIT].includes(data.actionType)
        ),
        switchMap(data => this.userProfileService.authCallback().pipe(map(profile => ({ data, profile })))),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: ({ data, profile }) => {
          if (data.actionType === NotificationActionType.REMOVED_FROM_UNIT) {
            const removedUnitId = data.metadata?.['unitId'];
            if (removedUnitId === this.assetQueryStore.assetUnit()) {
              this.assetQueryStore.saveAssetUnit('');
            }
          }
          this.authStore.setCurrentProfile(profile);
        }
      });
  }

  #setupCompletedLoginStream(): void {
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        tap(() => this.authService.checkAndSetActiveAccount()),
        filter(() => Boolean(this.msalAuthService.instance.getActiveAccount())),
        switchMap(() => this.userProfileService.authCallback()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: profile => this.authStore.setCurrentProfile(profile)
      });
  }

  #setupMsalRedirect(): void {
    this.msalAuthService.handleRedirectObservable().subscribe(noop);
  }
}
